import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { SPREEDLY_CHALLENGE_EL_ID, SPREEDLY_FINGERPRINT_EL_ID } from '../../../hooks/payment/useSpreedlyThreeDs';

interface Props {
  active: boolean;
  onCancel?: () => void;
}

/**
 * The two DOM anchors Spreedly's 3DS2 lifecycle mounts into. Both must exist
 * BEFORE the lifecycle starts, so they are always rendered and only toggled
 * visible — the fingerprint iframe stays hidden for good, the challenge overlay
 * appears only when the issuer demands interaction.
 *
 * The overlay is the customer's whole screen while a challenge runs, and the
 * bank's frame has no exit of its own — so the panel carries a cancel control
 * (button + Escape) that hands the decision back to the 3DS hook.
 */
export default function SpreedlyChallengeContainer(props: Props) {
  const { active, onCancel } = props;
  const { t } = useTranslation(null, { keyPrefix: 'Components:Payment:Spreedly:SpreedlyChallengeContainer' });

  useEffect(() => {
    if (!active || !onCancel) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCancel();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [active, onCancel]);

  return (
    <>
      <div id={SPREEDLY_FINGERPRINT_EL_ID} className="mp-block hidden" />
      <div className={classnames(overlay, { hidden: !active })} data-test="spreedly-challenge-overlay">
        <div className={panel}>
          <p className={hint}>{t('hint')}</p>
          <div id={SPREEDLY_CHALLENGE_EL_ID} className={challengeFrame} />
          {onCancel && (
            <button type="button" className={cancelButton} onClick={onCancel} data-test="spreedly-challenge-cancel">
              {t('cancel')}
            </button>
          )}
        </div>
      </div>
    </>
  );
}

const overlay = 'fixed inset-0 z-[1000] bg-black/50 flex items-center justify-center p-md';
const panel = 'bg-brand-background-primary rounded-lg p-md w-full max-w-[540px]';
const hint = 'text-sm text-center mb-sm';
// mp-block: the issuer's challenge iFrame mounts here and can carry an OTP or bank credentials.
// Session replay must never record it — as for the card-number/CVV boxes and the Stripe/Adyen frames.
const challengeFrame = 'mp-block w-full h-[600px] max-h-[70vh] overflow-auto';
const cancelButton =
  'mt-sm block w-full text-center text-sm font-medium underline underline-offset-2 text-brand-text-primary hover:opacity-80 dark:text-white';
